import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import {
    Checkbox,
    FormControlLabel,
    FormGroup,
    makeStyles,
    Radio,
    withStyles,
} from '@material-ui/core';
import { Check } from '@material-ui/icons';
import { green, white } from '@avangard/ui/colors';
import { breakpoints } from '@avangard/ui/utils';

import { ZoomImage } from '@modules/layout/moleculas';
import { Photo } from '@modules/testing/atoms';

type TestingAnswersProps = {
    disabled?: boolean;
    enableCorrectAnswers?: boolean;
    hasMultipleCorrectAnswers?: boolean;
    title: string;
    answerIds?: Array<string>;
    answers?: Array<{
        id: string;
        text: string;
        isCorrect?: boolean;
        photo: {
            exists: () => boolean;
            origin: string;
        };
    }>;
    onChange?: (answerId: string) => (_: React.ChangeEvent<{}>) => void;
    actions?: React.ReactNode;
};

const Inner = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    padding: 54px;
    border: 1px solid ${white[70]};
    border-radius: 2px;

    ${breakpoints.down('sm')} {
        padding: 24px;
    }
`;

const Answers = styled(Inner)`
    margin-top: 32px;
    padding: 54px;
    border: 1px solid ${white[70]};
    border-radius: 2px;

    ${breakpoints.down('sm')} {
        padding: 24px;
        margin-top: 16px;
    }
`;

const AnswersTitle = styled.div`
    width: 100%;
    margin-bottom: 30px;
    font-size: 1.5rem;

    ${breakpoints.down('sm')} {
        font-size: 1.3rem;
        margin-bottom: 14px;
    }
`;

const AnswersList = styled.div`
    > * {
        &:not(:last-child) {
            margin-bottom: 16px;
        }
    }

    ${breakpoints.down('sm')} {
        &:not(:last-child) {
            margin-bottom: 10px;
        }
    }
`;

const AnswersActions = styled.div`
    width: 100%;
    margin-top: 32px;

    ${breakpoints.down('sm')} {
        background: ${white[100]};
        display: flex;
        align-items: center;
        justify-content: center;
        position: fixed;
        z-index: 3;
        left: 0;
        bottom: 0;
        width: 100%;
        padding: 16px 16px 8px;
        Button {
            height: 48px;
            width: 100%;
        }
    }
`;

const AnswerPhoto = styled(Photo)<{ dense?: boolean }>`
    margin-top: 12px;
    padding-inline-start: 32px;

    ${p =>
        !!p.dense &&
        css`
            margin: 0;
            padding: 0;
        `}
`;

const StyledFormGroup = withStyles({
    root: {
        position: 'relative',
    },
})(FormGroup);

const StyledFormControlLabel = withStyles(theme => ({
    root: {
        marginInlineEnd: 0,
    },

    label: {
        fontSize: '2rem',
        fontWeight: 700,

        [theme.breakpoints.down('xs')]: {
            fontSize: '1.4rem',
        },
    },
}))(FormControlLabel);

const useCheckInputStyles = makeStyles({
    root: {
        marginRight: 12,
        padding: 0,
    },
});

const CorrectIcon = styled.div`
    z-index: 0;
    position: absolute;
    left: -32px;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 20px;
    width: 20px;
    flex: 0 0 auto;

    ${breakpoints.down('sm')} {
        left: 0;

        & + * {
            margin-left: 26px;
        }
    }
`;

const checkIcon = <Check style={{ fontSize: 16, color: green[100] }} />;

const TestingAnswers = memo((props: TestingAnswersProps): React.ReactElement => {
    const {
        disabled,
        enableCorrectAnswers,
        hasMultipleCorrectAnswers,
        answers = [],
        answerIds = [],
        title,
        actions,
        onChange,
    } = props;

    const checkInputClasses = useCheckInputStyles();

    return (
        <Answers>
            <AnswersTitle>{title}</AnswersTitle>

            <AnswersList>
                {answers.map(answer => {
                    const controlProps = {
                        classes: checkInputClasses,
                        checked: answerIds?.includes(answer.id),
                        onChange: onChange?.(answer.id),
                    };

                    const hasLabel = !!answer.text;

                    const renderedLabel = hasLabel ? (
                        answer.text
                    ) : answer.photo.exists() ? (
                        <AnswerPhoto dense>
                            <ZoomImage hasMagnifierIcon src={answer.photo.origin} />
                        </AnswerPhoto>
                    ) : null;

                    return (
                        <StyledFormGroup row key={answer.id}>
                            {enableCorrectAnswers ? (
                                <CorrectIcon>{answer.isCorrect ? checkIcon : <></>}</CorrectIcon>
                            ) : null}
                            <StyledFormControlLabel
                                disabled={disabled}
                                label={renderedLabel}
                                control={
                                    hasMultipleCorrectAnswers ? (
                                        <Checkbox {...controlProps} />
                                    ) : (
                                        <Radio {...controlProps} />
                                    )
                                }
                            />

                            {hasLabel && answer.photo.exists() ? (
                                <AnswerPhoto>
                                    <ZoomImage hasMagnifierIcon src={answer.photo.origin} />
                                </AnswerPhoto>
                            ) : null}
                        </StyledFormGroup>
                    );
                })}
            </AnswersList>

            {actions ? <AnswersActions>{actions}</AnswersActions> : null}
        </Answers>
    );
});

export { TestingAnswers };
